import React from 'react';
import { MessageSquare, Send, CheckCircle, RefreshCw, Plus, Edit2, Trash2, X, Image as ImageIcon } from 'lucide-react';
import { useFeedbackPage } from './useFeedbackPage';
import { FeedbackNotificationModal } from './FeedbackNotificationModal';
import { FeedbackDeleteConfirmModal } from './FeedbackDeleteConfirmModal';

const FeedbackPage: React.FC = () => {
  const {
    feedbackFormData,
    setFeedbackFormData,
    feedbackImagePreviews,
    feedbackImageNames,
    uploadingFeedbackImage,
    feedbackLoading,
    loadingFeedback,
    feedbacks,
    showFeedbackForm,
    setShowFeedbackForm,
    editingFeedbackId,
    openAddForm,
    feedbackMessage,
    setFeedbackMessage,
    showDeleteConfirm,
    setShowDeleteConfirm,
    loadFeedback,
    getFilenameFromUrl,
    handleFeedbackImageChange,
    removeFeedbackImage,
    handleFeedbackSubmit,
    handleEditFeedback,
    handleDeleteFeedback,
    confirmDeleteFeedback,
    closeForm,
  } = useFeedbackPage();

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 -top-32 w-[400px] h-[400px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"></div>
        <div className="absolute -left-32 -bottom-32 w-[400px] h-[400px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"></div>
      </div>
      <div className="relative px-4 lg:px-6 py-4 lg:py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-50 mb-1">
            Feedback
          </h1>
          <p className="text-sm text-gray-400">Share your thoughts and suggestions</p>
        </div>

        {feedbackMessage && (
          <FeedbackNotificationModal message={feedbackMessage} onClose={() => setFeedbackMessage(null)} />
        )}
        {showDeleteConfirm && (
          <FeedbackDeleteConfirmModal onConfirm={confirmDeleteFeedback} onCancel={() => setShowDeleteConfirm(false)} />
        )}

        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-white">My Feedback</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={openAddForm}
                className="flex items-center space-x-1.5 px-3 py-1.5 text-sm bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add</span>
              </button>
              <button
                onClick={loadFeedback}
                disabled={loadingFeedback}
                className="p-1 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${loadingFeedback ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {loadingFeedback ? (
            <div className="flex justify-center items-center py-8">
              <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : showFeedbackForm ? (
            <div className="mb-6 bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-xl p-5 shadow-xl">
              <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                <div>
                  <label htmlFor="subject" className="block text-xs font-medium text-gray-400 mb-1.5">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    required
                    value={feedbackFormData.subject}
                    onChange={(e) => setFeedbackFormData({ ...feedbackFormData, subject: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                    placeholder="Brief description"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs font-medium text-gray-400 mb-1.5">Message</label>
                  <textarea
                    id="message"
                    required
                    rows={4}
                    value={feedbackFormData.message}
                    onChange={(e) => setFeedbackFormData({ ...feedbackFormData, message: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 resize-none transition-all"
                    placeholder="Your feedback..."
                  />
                </div>
                <div>
                  <label htmlFor="feedback-image" className="block text-xs font-medium text-gray-400 mb-1.5">Attachments (Optional)</label>
                  {feedbackImagePreviews.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {feedbackImagePreviews.map((preview, originalIndex) => {
                        const isValid = preview.startsWith('data:') || preview.startsWith('http://') || preview.startsWith('https://') || preview.startsWith('/api/');
                        if (!isValid) return null;
                        const imageUrl = preview.startsWith('/api/') ? `${process.env.REACT_APP_API_URL || ''}${preview}` : preview;
                        const fileName = feedbackImageNames[originalIndex] || getFilenameFromUrl(imageUrl);
                        return (
                          <div key={originalIndex} className="relative inline-block">
                            <div className="relative">
                              <img src={imageUrl} alt={`Preview ${originalIndex + 1}`} className="max-w-xs h-auto rounded-lg border border-gray-700/50 max-h-32 object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                              <button type="button" onClick={() => removeFeedbackImage(originalIndex)} className="absolute top-1 right-1 p-1 bg-red-500/80 hover:bg-red-600 rounded-full text-white">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                            <p className="text-xs text-gray-400 mt-1 truncate max-w-[200px]" title={fileName}>{fileName}</p>
                          </div>
                        );
                      }).filter(Boolean)}
                    </div>
                  )}
                  <label htmlFor="feedback-image" className="flex items-center justify-center w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-900/70 transition-colors">
                    <ImageIcon className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="text-sm text-gray-400">Choose attachments</span>
                    <input id="feedback-image" type="file" accept="image/*" multiple onChange={handleFeedbackImageChange} className="hidden" />
                  </label>
                </div>
                <div className="flex items-center justify-end space-x-2">
                  <button type="button" onClick={closeForm} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={feedbackLoading || uploadingFeedbackImage}
                    className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {(feedbackLoading || uploadingFeedbackImage) ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{uploadingFeedbackImage ? 'Uploading image...' : (editingFeedbackId ? 'Updating...' : 'Submitting...')}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>{editingFeedbackId ? 'Update' : 'Submit'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          ) : feedbacks.length > 0 ? (
            <div className="space-y-3">
              {feedbacks.map((feedback) => (
                <div key={feedback.id} className="bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-lg p-3 hover:border-cyan-500/30 transition-all">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="text-sm font-semibold text-white truncate">{feedback.subject}</h3>
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                          <button
                            type="button"
                            onClick={() => handleEditFeedback(feedback)}
                            className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 rounded transition-colors"
                          >
                            <Edit2 className="w-2.5 h-2.5" />
                            <span>Edit</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteFeedback(feedback)}
                            className="flex items-center space-x-1 px-2 py-1 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded transition-colors"
                          >
                            <Trash2 className="w-2.5 h-2.5" />
                            <span>Delete</span>
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-300 whitespace-pre-wrap mb-2 line-clamp-2">{feedback.message}</p>
                      {feedback.image_url && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {feedback.image_url.split(',').map((url: string) => url.trim()).filter((url: string) => url && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/api/'))).map((url: string, index: number) => {
                            const imageUrl = url.startsWith('/api/') ? `${process.env.REACT_APP_API_URL || ''}${url}` : url;
                            const fileName = getFilenameFromUrl(imageUrl);
                            return (
                              <div key={index} className="inline-block">
                                <img
                                  src={imageUrl}
                                  alt={`Feedback attachment ${index + 1}`}
                                  className="max-w-[200px] h-auto rounded border border-gray-700/50 max-h-24 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(imageUrl, '_blank', 'noopener,noreferrer')}
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                                <p className="text-xs text-gray-400 mt-1 truncate max-w-[200px]" title={fileName}>{fileName}</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">
                          {new Date(feedback.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {feedback.updated_at && feedback.updated_at !== feedback.created_at && (
                            <span className="ml-1.5">• Updated {new Date(feedback.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          )}
                        </span>
                        {feedback.admin_notes && (
                          <span className="text-cyan-400 flex items-center space-x-0.5">
                            <CheckCircle className="w-2.5 h-2.5" />
                            <span>Admin responded</span>
                          </span>
                        )}
                      </div>
                      {feedback.admin_notes && (
                        <div className="mt-2 pt-2 border-t border-gray-700/50">
                          <p className="text-xs text-gray-400 mb-0.5">Admin Response:</p>
                          <p className="text-xs text-gray-300 whitespace-pre-wrap line-clamp-2">{feedback.admin_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-xl p-12 text-center">
              <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 text-sm mb-4">No feedback submitted yet</p>
              <button
                onClick={openAddForm}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-cyan-500/20 mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span className="font-medium text-sm">Add Feedback</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
